import React from 'react';
import { ChevronRight, Award, BarChart3, Calendar, Target, PlusCircle, User } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const WelcomeScreen = ({ onContinue }) => {
  const { currentUser } = useAuth();
  
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <header className="bg-blue-600 text-white py-8 px-4 text-center">
        <h1 className="text-3xl font-bold">Welcome to JStats</h1>
        <p className="text-lg mt-2 opacity-90">Your personal baseball statistics companion</p>
      </header>
      
      <main className="flex-grow flex flex-col items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-md p-6 w-full max-w-md">
          {/* Welcome message */}
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <User size={32} className="text-blue-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-800">
              Hi{currentUser?.displayName ? `, ${currentUser.displayName.split(' ')[0]}` : ''}!
            </h2>
            <p className="text-gray-600 mt-2">
              Let's set up your player profile to get started tracking your baseball stats.
            </p>
          </div>
          
          {/* App features */}
          <div className="mb-6">
            <h3 className="font-medium text-gray-700 mb-3">With JStats, you can:</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <div className="bg-green-100 p-2 rounded-full mr-3">
                  <BarChart3 size={18} className="text-green-600" />
                </div>
                <div>
                  <span className="font-medium">Track Your Stats</span>
                  <p className="text-sm text-gray-600">Record and analyze your batting and pitching statistics</p>
                </div>
              </li>
              <li className="flex items-start">
                <div className="bg-blue-100 p-2 rounded-full mr-3">
                  <Calendar size={18} className="text-blue-600" />
                </div>
                <div>
                  <span className="font-medium">Manage Your Schedule</span>
                  <p className="text-sm text-gray-600">Keep track of upcoming games and practices</p>
                </div>
              </li>
              <li className="flex items-start">
                <div className="bg-purple-100 p-2 rounded-full mr-3">
                  <Award size={18} className="text-purple-600" />
                </div>
                <div>
                  <span className="font-medium">Set Milestones</span>
                  <p className="text-sm text-gray-600">Create personal goals and track your achievements</p>
                </div>
              </li>
              <li className="flex items-start">
                <div className="bg-orange-100 p-2 rounded-full mr-3">
                  <Target size={18} className="text-orange-600" />
                </div>
                <div>
                  <span className="font-medium">Analyze Performance</span>
                  <p className="text-sm text-gray-600">Get insights into your strengths and areas for improvement</p>
                </div>
              </li>
            </ul>
          </div>
          
          {/* Setup process */}
          <div className="mb-6">
            <h3 className="font-medium text-gray-700 mb-3">Quick 3-step setup:</h3>
            <ol className="space-y-2">
              <li className="flex items-center">
                <div className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  1
                </div>
                <span>Enter your basic information</span>
              </li>
              <li className="flex items-center">
                <div className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  2
                </div>
                <span>Select your playing positions</span>
              </li>
              <li className="flex items-center">
                <div className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  3
                </div>
                <span>Choose your leagues</span>
              </li>
            </ol>
          </div>
          
          {/* Continue button */}
          <button
            onClick={onContinue}
            className="w-full flex items-center justify-center bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Let's Get Started <ChevronRight size={20} className="ml-2" />
          </button>
        </div>
      </main>
      
      {/* Footer */}
      <footer className="bg-white py-4 px-6 border-t text-center text-gray-500 text-sm">
        &copy; {new Date().getFullYear()} JStats. All rights reserved.
      </footer>
    </div>
  );
};

export default WelcomeScreen;
